import { useState, type KeyboardEvent } from 'react'
import { Document, Page } from 'react-pdf'
import { cn } from '@klear/ui/lib/utils'
import { PDF_OPTIONS } from '@/app/document-review/pdf-document-view'
import { isImageDocument, isPdfDocument } from '@/app/document-review/source-document-file'
import { plural } from '@/app/receivables/fund-checks'

export type StripTile = {
  key: string
  title: string
  file: string
  url: string | null
  status: string
  tone: 'ok' | 'warn' | 'bad'
  checkCount: number
  current: boolean
  onPick: () => void
}

const TONE_TEXT: Record<StripTile['tone'], string> = {
  ok: 'text-success',
  warn: 'text-warning',
  bad: 'text-danger'
}

const TONE_DOT: Record<StripTile['tone'], string> = {
  ok: 'bg-success',
  warn: 'bg-warning',
  bad: 'bg-danger'
}

function Thumbnail({ tile }: { tile: StripTile }) {
  const [failed, setFailed] = useState(false)
  const box = 'grid h-[68px] w-[52px] shrink-0 place-items-center overflow-hidden rounded-sm border bg-white'
  if (tile.url == null) return <span className={cn(box, 'border-dashed border-muted-foreground/60')} aria-hidden />
  if (isImageDocument(tile.file) && !failed) {
    return (
      <span className={box} aria-hidden>
        <img src={tile.url} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      </span>
    )
  }
  if (isPdfDocument(tile.file) && !failed) {
    return (
      <span className={box} aria-hidden>
        <Document file={tile.url} options={PDF_OPTIONS} onLoadError={() => setFailed(true)} loading={null} error={null}>
          <Page pageNumber={1} width={50} renderTextLayer={false} renderAnnotationLayer={false} loading={null} />
        </Document>
      </span>
    )
  }
  return null
}

function moveFocus(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
  const tiles = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-tile]')]
  const at = tiles.findIndex((tile) => tile === document.activeElement)
  if (at < 0) return
  event.preventDefault()
  tiles[Math.min(tiles.length - 1, Math.max(0, at + (event.key === 'ArrowRight' ? 1 : -1)))]?.focus()
}

export function DocumentStrip({
  label,
  tiles,
  showCounts
}: {
  label: string
  tiles: readonly StripTile[]
  showCounts: boolean
}) {
  if (tiles.length === 0) return null
  const tabStop = tiles.some((tile) => tile.current) ? undefined : tiles[0]?.key
  return (
    <nav aria-label={label} className="min-w-0">
      <div className="flex gap-2 overflow-x-auto pb-1" onKeyDown={moveFocus}>
        {tiles.map((tile) => {
          const { current } = tile
          return (
            <button
              key={tile.key}
              type="button"
              data-tile
              tabIndex={current || tile.key === tabStop ? 0 : -1}
              aria-current={current ? 'true' : undefined}
              onClick={tile.onPick}
              className={cn(
                'flex w-[248px] shrink-0 items-center gap-2.5 rounded-lg border bg-background p-2 text-left text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                current ? 'border-foreground ring-1 ring-inset ring-foreground' : 'hover:bg-muted/50'
              )}
            >
              <Thumbnail tile={tile} />
              <span className="grid min-w-0 gap-0.5">
                <span className="truncate font-semibold">{tile.title}</span>
                <span className="truncate text-xs text-muted-foreground">{tile.file}</span>
                <span className="flex items-center gap-1.5 text-xs">
                  <span className={cn('h-1.5 w-1.5 rounded-full', TONE_DOT[tile.tone])} aria-hidden />
                  <span className={cn('font-medium', TONE_TEXT[tile.tone])}>{tile.status}</span>
                  {showCounts && tile.checkCount > 0 ? (
                    <span className="text-muted-foreground">· {plural(tile.checkCount, 'check')}</span>
                  ) : null}
                </span>
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
